import React from 'react';
import { WithTranslation, withTranslation, Trans } from 'react-i18next';
import styled from 'styled-components/macro';
import { Formik } from 'formik';

import { H1, P } from '../../../../components/Typography';
import Modal from '../../../../components/Modal';
import { Button, ButtonExternalLink, ButtonGroup } from '../../../../components/Buttons';
import { distances, grid } from '../../../../styles/constants';
import { State as VippsState, VippsModalState } from '../reducer';
import { LoadingOverlay } from '../../../../components/Loading';
import { AccountIdsProps, withAccountIds } from '../../../../auth/accessToken/withAccountIds';
import { Input, ValidationState } from '../../../../components/Forms';
import { getValueAt } from '../../../../helpers/getValueAt';
import { Account } from '../../../../types/management-auth';

import { validate, validateRequired, validateUrl } from '../../../../helpers/validation';

interface ValidatedInputProps extends WithTranslation {
    values: any;
    initialValues: any;
    errors: any;
    path: string;
    onChange: (e: React.FormEvent<HTMLInputElement>) => void;
    onBlur: (e: React.FormEvent<HTMLInputElement>) => void;
    touched: any;
    required?: boolean;
    prefix?: string;
    maxlength?: number;
    formatter?: (value: string) => string;
}
const ValidatedInput = (
    {
        t,
        values,
        initialValues,
        errors,
        path,
        onChange,
        onBlur,
        touched,
        required,
        prefix,
        maxlength,
    }: ValidatedInputProps
) => {
    const value = getValueAt(path, values);
    const initialValue = getValueAt(path, initialValues);
    const error = getValueAt(path, errors);
    const was_touched = getValueAt(path, touched);
    const validation =
        (was_touched || initialValue) && error
            ? {
                state: ValidationState.Invalid,
                message: error,
            }
            : undefined;
    const label = required
        ? `${t(`settings.payment_connections.vipps_modal.fields.${path}`)} * `
        : t(`settings.payment_connections.vipps_modal.fields.${path}`);
    return (
        <InputWrapper>
            <Input
                label={label}
                placeholder={t(`settings.payment_connections.vipps_modal.placeholders.${path}`)}
                type="text"
                name={path}
                value={value || ''}
                onChange={(e) => {
                    onChange(e);
                }}
                onBlur={(e) => {
                    onChange(e);
                    onBlur(e);
                }}
                validation={validation}
                prefix={prefix}
                maxlength={maxlength}
            />
        </InputWrapper>
    );
};

const TranslatedValidatedInput = withTranslation()(ValidatedInput);

export interface VippsModalProps extends WithTranslation, AccountIdsProps {
    initiateVippsConnection: (accountId: string) => void;
    updateAccountAndInitiateVippsConnection: (accountId: string, account: Account) => void;
    closeVippsModal: () => void;
    openVippsForm: () => void;
    vipps: VippsState;
}

const VippsModal = (
    {
        t,
        vipps,
        closeVippsModal,
        openVippsForm,
        initiateVippsConnection,
        updateAccountAndInitiateVippsConnection,
        prodAccountId,
    }: VippsModalProps
) => {
    if (!vipps.account) {
        return null;
    }

    if (!prodAccountId) {
        return null;
    }

    if (vipps.vippsModalState === VippsModalState.Closed) {
        return null;
    }

    return (
        <Modal fixedWidth>
            {[VippsModalState.PreApplicationInfo, VippsModalState.InitiateApplicationLoading].includes(
                vipps.vippsModalState
            ) &&
                vipps.account &&
                !vipps.account?.company.website && (
                <>
                    <H1>{t('settings.payment_connections.vipps_modal.title_intro')}</H1>
                    <Formik
                        enableReinitialize
                        initialValues={{ website: 'https://' }}
                        initialErrors={{ website: undefined }}
                        validate={(values) => {
                            let errors: { [key: string]: string } = {};
                            if (!values || Object.keys(values).length === 0) {
                                // this should not happen but...
                                return { error: 'account not loaded yet...' };
                            }

                            const validationValues = { ...values };
                            if (values.website === 'https://') {
                                validationValues.website = '';
                            }

                            const validators = [
                                {
                                    path: 'website',
                                    validator: validateRequired(
                                        t('settings.payment_connections.vipps_modal.invalid.website.required')
                                    ),
                                },
                                {
                                    path: 'website',
                                    validator: validateUrl(
                                        t('settings.payment_connections.vipps_modal.invalid.website.format')
                                    ),
                                },
                            ];

                            errors = validators.reduce((acc, elem) => {
                                return validate(elem.path, elem.validator, validationValues, acc);
                            }, errors);

                            return errors;
                        }}
                        onSubmit={(values) => {
                            if (vipps.account) {
                                const updatedAccount = vipps.account;
                                updatedAccount.company.website = values.website;
                                updateAccountAndInitiateVippsConnection(prodAccountId, updatedAccount);
                            }
                        }}
                    >
                        {({ values, errors, handleChange, handleBlur, touched, handleSubmit, isValid }) => {
                            return (
                                <form onSubmit={handleSubmit}>
                                    <SpacedWrapper>
                                        <TranslatedValidatedInput
                                            path="website"
                                            initialValues={{ website: 'https://' }}
                                            values={values}
                                            errors={errors}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            touched={touched}
                                            required
                                        />
                                    </SpacedWrapper>
                                    <SpacedWrapper>
                                        <P>{t('settings.payment_connections.vipps_modal.intro')}</P>
                                    </SpacedWrapper>
                                    <ButtonGroup>
                                        <Button type="submit" disabled={!isValid}>
                                            {t(
                                                'settings.payment_connections.vipps_modal.sign_up_with_vipps_call_to_action'
                                            )}
                                        </Button>
                                        <Button className="alt" onClick={closeVippsModal}>
                                            {t('settings.payment_connections.vipps_modal.cancel')}
                                        </Button>
                                    </ButtonGroup>
                                </form>
                            );
                        }}
                    </Formik>
                    {vipps.vippsModalState === VippsModalState.InitiateApplicationLoading && <LoadingOverlay />}
                </>
            )}
            {[VippsModalState.PreApplicationInfo, VippsModalState.InitiateApplicationLoading].includes(
                vipps.vippsModalState
            ) &&
                vipps.account &&
                vipps.account?.company.website && (
                <>
                    <H1>{t('settings.payment_connections.vipps_modal.title_intro')}</H1>
                    <SpacedWrapper>
                        <P>
                            <Trans i18nKey="settings.payment_connections.vipps_modal.intro" />
                        </P>
                    </SpacedWrapper>
                    <ButtonGroup>
                        <Button onClick={() => initiateVippsConnection(prodAccountId)}>
                            {t('settings.payment_connections.vipps_modal.sign_up_with_vipps_call_to_action')}
                        </Button>
                        <Button className="alt" onClick={closeVippsModal}>
                            {t('settings.payment_connections.vipps_modal.cancel')}
                        </Button>
                    </ButtonGroup>
                    {vipps.vippsModalState === VippsModalState.InitiateApplicationLoading && <LoadingOverlay />}
                </>
            )}
            {vipps.vippsModalState === VippsModalState.ShowFormLink && vipps.vippsConnectionResponse && (
                <>
                    <H1>{t('settings.payment_connections.vipps_modal.title_open_form')}</H1>

                    <SpacedWrapper>
                        <P>
                            <Trans i18nKey="settings.payment_connections.vipps_modal.open_form" />
                        </P>
                    </SpacedWrapper>

                    <ButtonGroup>
                        <ButtonExternalLink
                            href={vipps.vippsConnectionResponse.signup.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={openVippsForm}
                        >
                            {t('settings.payment_connections.vipps_modal.open_form_call_to_action')}
                        </ButtonExternalLink>
                    </ButtonGroup>
                    <SpacedWrapper>
                        <br />
                        <small>
                            <Trans i18nKey="settings.payment_connections.vipps_modal.open_form_more" />
                        </small>
                    </SpacedWrapper>
                </>
            )}
            {vipps.vippsModalState === VippsModalState.ApplicationFormOpened && (
                <>
                    <H1>{t('settings.payment_connections.vipps_modal.title_last_step')}</H1>

                    <SpacedWrapper>
                        <P>
                            <Trans i18nKey="settings.payment_connections.vipps_modal.last_step" />
                        </P>
                    </SpacedWrapper>

                    <ButtonGroup>
                        <Button onClick={closeVippsModal}>{t('settings.payment_connections.vipps_modal.close')}</Button>
                    </ButtonGroup>
                </>
            )}
            {vipps.vippsModalState === VippsModalState.VippsApiError && (
                <>
                    <H1>{t('settings.payment_connections.vipps_modal.title_api_error')}</H1>

                    <SpacedWrapper>
                        <P>
                            <Trans i18nKey="settings.payment_connections.vipps_modal.api_error" />
                        </P>
                    </SpacedWrapper>

                    <ButtonGroup>
                        <Button onClick={closeVippsModal}>{t('settings.payment_connections.vipps_modal.close')}</Button>
                    </ButtonGroup>
                </>
            )}
        </Modal>
    );
};

export default withAccountIds(withTranslation()(VippsModal));

const SpacedWrapper = styled.div`
    margin-top: ${distances.small};
    margin-bottom: ${distances.normal};
`;

const InputWrapper = styled.div`
    margin-right: ${grid.gutter};

    &:last-child {
        margin: 0;
    }
`;
